import React, { useCallback, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const AI_PROMPT_TEXT = `I'm attaching a JSON file that has a list of sections, each with a list of {title, value} fields. Some "value" fields are empty strings. Please fill in every empty "value" with an accurate translation of its "title" (and translate any non-empty placeholder values too, if present), keeping the exact same JSON structure, field order, and section order. Do not add, remove, or rename any fields or sections. Reply with only the completed JSON file, ready to download/copy as-is.`;

// Lets an admin download a blank {title, value:""} template mirroring the
// current Manage Language form, get it translated elsewhere (an AI chat,
// a professional translator, whatever), and drop the filled-in file back
// in. This only fills values into the in-memory form via onImport — it
// does not save anything itself; the existing Save button in the parent
// dialog still does that, so the admin can review before persisting.
export default function LangImportDlg({ open, setOpen, langSettings, onImport }) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError("Could not copy to clipboard — please select and copy the text manually.");
    }
  };

  const handleDownloadTemplate = () => {
    const template = langSettings.map((section) => ({
      _title: section._title,
      data: (section.data || []).map((f) => ({ title: f.title, value: "" })),
    }));
    const blob = new Blob([JSON.stringify(template, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "language-import-template.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const processFile = useCallback(
    (file) => {
      setError("");
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        let parsed;
        try {
          parsed = JSON.parse(e.target.result);
        } catch (err) {
          setError("Could not parse this file as valid JSON.");
          return;
        }
        if (!Array.isArray(parsed)) {
          setError("Invalid file: expected a JSON array of sections.");
          return;
        }
        let filledCount = 0;
        const merged = langSettings.map((section) => {
          const importedSection = parsed.find(
            (s) => s && s._title === section._title
          );
          if (!importedSection || !Array.isArray(importedSection.data)) {
            return section;
          }
          return {
            ...section,
            data: (section.data || []).map((field) => {
              const importedField = importedSection.data.find(
                (f) => f && f.title === field.title
              );
              if (
                importedField &&
                typeof importedField.value === "string" &&
                importedField.value.trim() !== ""
              ) {
                filledCount++;
                return { ...field, value: importedField.value };
              }
              return field;
            }),
          };
        });
        if (filledCount === 0) {
          setError(
            "No matching, non-empty values were found in this file — nothing was imported."
          );
          return;
        }
        onImport(merged);
        setOpen(false);
      };
      reader.readAsText(file);
    },
    [langSettings, onImport, setOpen]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) processFile(file);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Import Translations
        <IconButton onClick={() => setOpen(false)} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          1. Download the template below.
          <br />
          2. Give it to an AI chat (or a translator) and ask it to fill in the
          empty "value" for each entry with the translation of that entry's
          "title", keeping the same JSON structure.
          <br />
          3. Drop the filled-in file below. Values are filled into the form —
          nothing is saved until you click Save.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          fullWidth
          sx={{ mb: 2 }}
        >
          Download Import Template
        </Button>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            mb: 2,
            bgcolor: "action.hover",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Copy this prompt, attach the downloaded file, and paste it into your AI chat:
            </Typography>
            <Button
              size="small"
              onClick={handleCopyPrompt}
              startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              color={copied ? "success" : "primary"}
              sx={{ flexShrink: 0, ml: 1 }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              color: "text.primary",
            }}
          >
            {AI_PROMPT_TEXT}
          </Typography>
        </Box>
        <Box
          component="label"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            display: "block",
            border: "2px dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            bgcolor: dragOver ? "action.hover" : "transparent",
            cursor: "pointer",
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
          <Typography variant="body2">
            {fileName ||
              "Drag & drop the filled-in JSON file here, or click to browse"}
          </Typography>
          <input
            type="file"
            accept="application/json"
            hidden
            onChange={handleFileInput}
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
